import { trackEvent } from '@pm/analytics';
import { Text, TextLink } from '@pm/ui';
import { Trans, useTranslation } from 'react-i18next';
import { usePatientProfile } from '../../hooks/profile';
import StepGroup from './StepGroup';

const LAB_KIT_VIDEO_LINK = 'https://youtu.be/cRmHLDP2xTw';
const NEAREST_LAB_LINK =
  'https://www.google.ca/search?q=blood+testing+lab+near+me';

export const LabInstructionsPage = () => {
  const { isUsaPatient } = usePatientProfile();
  const { t } = useTranslation('labs', {
    keyPrefix: 'LabInstructionsPage',
  });
  const { t: tGlobal } = useTranslation('global', {
    keyPrefix: 'SupportLinks',
  });

  const handleVideoInstructionsClick = () => {
    trackEvent('Button Clicked', {
      buttonText: t('LabKit.Step1.ButtonText'),
      buttonName: 'video_instructions',
      currentPath: window.location.pathname,
      targetPath: LAB_KIT_VIDEO_LINK,
      currentPageName: 'Lab Instructions',
    });
  };

  const handleNearestLabClickCA = () => {
    trackEvent('Button Clicked', {
      buttonText: t('LabReqCA.Step2.ButtonText'),
      buttonName: 'visit_nearest_lab',
      currentPath: window.location.pathname,
      targetPath: NEAREST_LAB_LINK,
      currentPageName: 'Lab Instructions',
    });
  };

  const videoInstructionsLink = (
    <Trans ns="labs" i18nKey="LabInstructionsPage.LabKit.Step1.Description">
      <TextLink
        to={LAB_KIT_VIDEO_LINK}
        external
        rel="noopener noreferrer"
        onClick={handleVideoInstructionsClick}
      />
    </Trans>
  );

  const nearestLabLinkCA = (
    <Trans ns="labs" i18nKey="LabInstructionsPage.LabReqCA.Step2.Description">
      <TextLink
        to={NEAREST_LAB_LINK}
        external
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleNearestLabClickCA}
      >
        {t('LabReqCA.Step2.Description')}
      </TextLink>
    </Trans>
  );

  const supportEmailLinkUS = (
    <Trans ns="labs" i18nKey="LabInstructionsPage.LabReqUS.Step3.Description">
      <TextLink to={tGlobal('USA.MailTo')} external />
    </Trans>
  );

  const labKitSteps = [
    { title: t('LabKit.Step1.Title'), description: videoInstructionsLink },
    {
      title: t('LabKit.Step2.Title'),
      description: t('LabKit.Step2.Description'),
    },
    {
      title: t('LabKit.Step3.Title'),
      description: t('LabKit.Step3.Description'),
    },
  ];

  const labReqStepsCA = [
    {
      title: t('LabReqCA.Step1.Title'),
      description: t('LabReqCA.Step1.Description'),
    },
    { title: t('LabReqCA.Step2.Title'), description: nearestLabLinkCA },
    {
      title: t('LabReqCA.Step3.Title'),
      description: t('LabReqCA.Step3.Description'),
    },
  ];

  const labReqStepsUS = [
    {
      title: t('LabReqUS.Step1.Title'),
      description: t('LabReqUS.Step1.Description'),
    },
    {
      title: t('LabReqUS.Step2.Title'),
      description: t('LabReqUS.Step2.Description'),
    },
    { title: t('LabReqUS.Step3.Title'), description: supportEmailLinkUS },
  ];

  return (
    <div className="pt-s">
      <div className="space-y-xs pb-s">
        <Text size="headingM" as="h1">
          {t('Title')}
        </Text>
        <Text size="bodyL">{t('Description1')}</Text>
        <Text size="bodyL">{t('Description2')}</Text>
      </div>

      <div className="border-b my-xl" />

      {isUsaPatient && (
        <>
          <section>
            <Text size="headingS" as="h2">
              {t('LabKit.Subtitle')}
            </Text>
            <div className="pt-s">
              <Text size="bodyL">{t('LabKit.Description')}</Text>
            </div>
            <div className="pt-xl">
              <div className="w-full rounded-s overflow-hidden">
                <iframe
                  className="w-full aspect-video"
                  src="https://www.youtube.com/embed/cRmHLDP2xTw"
                  title={t('LabKit.VideoTitle')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="pt-xl">
              <ol className="list-none">
                {labKitSteps.map((step, i) => (
                  <StepGroup
                    key={step.title}
                    title={step.title}
                    description={step.description}
                    liClassName={i !== 0 ? 'mt-m' : ''}
                  />
                ))}
              </ol>
            </div>
          </section>

          <div className="border-b my-xl" />
        </>
      )}

      <section>
        <Text size="headingS" as="h2">
          {t(isUsaPatient ? 'LabReqUS.Subtitle' : 'LabReqCA.Subtitle')}
        </Text>
        <div className="pt-s">
          <Text size="bodyL">
            {t(isUsaPatient ? 'LabReqUS.Description' : 'LabReqCA.Description')}
          </Text>
        </div>
        <div className="py-m">
          <ol className="list-none">
            {(isUsaPatient ? labReqStepsUS : labReqStepsCA).map((step, i) => (
              <StepGroup
                key={step.title}
                title={step.title}
                description={step.description}
                liClassName={i !== 0 ? 'mt-m' : ''}
              />
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
};
